import { extendTheme, type ThemeConfig } from '@chakra-ui/react'

const config: ThemeConfig = {
  initialColorMode: 'dark',
  useSystemColorMode: false,
}

const theme = extendTheme({
  config,
  fonts: {
    heading: `'Inter', system-ui, sans-serif`,
    body: `'Roboto', system-ui, sans-serif`,
    mono: `'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace`,
    editor: `'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace`,
  },
  colors: {
    brand: {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b',
      950: '#022c22',
    },
    stone: {
      0: '#ffffff',
      50: '#fafaf9',
      100: '#f5f5f4',
      200: '#e7e5e4',
      300: '#d6d3d1',
      400: '#a8a29e',
      500: '#78716c',
      600: '#57534e',
      700: '#44403c',
      800: '#292524',
      900: '#1c1917',
      950: '#0c0a09',
    },
  },
  semanticTokens: {
    colors: {
      // Background colors
      'bg.primary': {
        default: 'stone.50',
        _dark: 'stone.950',
      },
      'bg.surface': {
        default: 'white',
        _dark: 'stone.900',
      },
      'bg.subtle': {
        default: 'stone.100',
        _dark: 'stone.800',
      },
      'bg.muted': {
        default: 'stone.200',
        _dark: 'stone.800',
      },
      // Text colors
      'text.primary': {
        default: 'stone.900',
        _dark: 'stone.50',
      },
      'text.secondary': {
        default: 'stone.600',
        _dark: 'stone.400',
      },
      'text.muted': {
        default: 'stone.500',
        _dark: 'stone.500',
      },
      // Border colors
      'border.default': {
        default: 'stone.200',
        _dark: 'stone.700',
      },
      'border.subtle': {
        default: 'stone.100',
        _dark: 'stone.700',
      },
      // Editor specific
      'editor.bg': {
        default: 'white',
        _dark: 'stone.900',
      },
      'editor.sidebar': {
        default: 'stone.50',
        _dark: 'stone.800',
      },
      'editor.border': {
        default: 'stone.200',
        _dark: 'stone.700',
      },
    },
  },
  styles: {
    global: (props: { colorMode: string }) => ({
      body: {
        bg: props.colorMode === 'dark' ? '#0c0a09' : '#fafaf9',
        color: props.colorMode === 'dark' ? '#fafaf9' : '#1c1917',
        minHeight: '100vh',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      },
    }),
  },
  components: {
    Button: {
      defaultProps: {
        colorScheme: 'brand',
      },
      baseStyle: {
        fontFamily: `'Roboto', system-ui, sans-serif`,
        fontWeight: 500,
        borderRadius: '8px',
        transition: 'background 150ms ease',
      },
      variants: {
        solid: (_props: { colorMode: string }) => ({
          bg: 'brand.500',
          color: 'white',
          borderRadius: '8px',
          _hover: {
            bg: 'brand.600',
          },
          _active: {
            bg: 'brand.700',
          },
        }),
        outline: (props: { colorMode: string }) => ({
          borderRadius: '8px',
          borderColor: props.colorMode === 'dark' ? 'stone.700' : 'stone.300',
          color: props.colorMode === 'dark' ? 'stone.100' : 'stone.900',
          _hover: {
            bg: props.colorMode === 'dark' ? 'stone.800' : 'stone.100',
          },
        }),
        ghost: (props: { colorMode: string }) => ({
          borderRadius: '8px',
          color: props.colorMode === 'dark' ? 'stone.400' : 'stone.600',
          _hover: {
            bg: props.colorMode === 'dark' ? 'stone.800' : 'stone.100',
            color: props.colorMode === 'dark' ? 'stone.300' : 'stone.700',
          },
        }),
        danger: (props: { colorMode: string }) => ({
          borderRadius: '8px',
          bg: 'transparent',
          color: props.colorMode === 'dark' ? '#f87171' : '#ef4444',
          _hover: {
            bg: props.colorMode === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.08)',
          },
          _active: {
            bg: props.colorMode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.12)',
          },
        }),
      },
      sizes: {
        sm: { h: '32px', px: '14px', fontSize: '13px' },
        md: { h: '40px', px: '18px', fontSize: '14px' },
        lg: { h: '48px', px: '24px', fontSize: '16px' },
      },
    },
    Card: {
      baseStyle: (props: { colorMode: string }) => ({
        container: {
          borderRadius: '12px',
          boxShadow: 'none',
          bg: props.colorMode === 'dark' ? 'stone.900' : 'white',
          borderWidth: '1px',
          borderColor: props.colorMode === 'dark' ? 'stone.700' : 'stone.200',
        },
      }),
    },
    Input: {
      defaultProps: {
        focusBorderColor: 'brand.500',
      },
      variants: {
        outline: (props: { colorMode: string }) => ({
          field: {
            borderRadius: '8px',
            bg: 'transparent',
            borderColor: props.colorMode === 'dark' ? 'stone.700' : 'stone.300',
            _hover: {
              borderColor: props.colorMode === 'dark' ? 'stone.600' : 'stone.400',
            },
          },
        }),
      },
    },
    Textarea: {
      defaultProps: {
        focusBorderColor: 'brand.500',
      },
      variants: {
        outline: (props: { colorMode: string }) => ({
          borderRadius: '8px',
          bg: 'transparent',
          borderColor: props.colorMode === 'dark' ? 'stone.700' : 'stone.300',
          _hover: {
            borderColor: props.colorMode === 'dark' ? 'stone.600' : 'stone.400',
          },
        }),
      },
    },
    Select: {
      defaultProps: {
        focusBorderColor: 'brand.500',
      },
      variants: {
        outline: (props: { colorMode: string }) => ({
          field: {
            borderRadius: '8px',
            bg: 'transparent',
            borderColor: props.colorMode === 'dark' ? 'stone.700' : 'stone.300',
          },
        }),
      },
    },
    Tabs: {
      variants: {
        line: (props: { colorMode: string }) => ({
          tab: {
            color: props.colorMode === 'dark' ? 'stone.500' : 'stone.500',
            _selected: {
              color: props.colorMode === 'dark' ? 'brand.400' : 'brand.600',
              borderColor: 'brand.500',
              borderBottomWidth: '2px',
            },
          },
        }),
        'soft-rounded': (props: { colorMode: string }) => ({
          tab: {
            borderRadius: '8px',
            color: props.colorMode === 'dark' ? 'stone.400' : 'stone.600',
            _selected: {
              bg: props.colorMode === 'dark' ? 'stone.800' : 'white',
              color: props.colorMode === 'dark' ? 'stone.50' : 'stone.900',
            },
          },
          tablist: {
            bg: props.colorMode === 'dark' ? 'stone.900' : 'stone.100',
            borderRadius: '8px',
            p: 1,
          },
        }),
      },
      defaultProps: {
        variant: 'soft-rounded',
      },
    },
    Switch: {
      defaultProps: {
        colorScheme: 'brand',
      },
    },
    Modal: {
      baseStyle: (props: { colorMode: string }) => ({
        dialog: {
          borderRadius: '16px',
          bg: props.colorMode === 'dark' ? 'stone.900' : 'white',
        },
        overlay: {
          bg: 'blackAlpha.600',
          backdropFilter: 'blur(8px)',
        },
      }),
    },
    Table: {
      variants: {
        simple: (props: { colorMode: string }) => ({
          th: {
            borderColor: props.colorMode === 'dark' ? 'stone.700' : 'stone.200',
          },
          td: {
            borderColor: props.colorMode === 'dark' ? 'stone.700' : 'stone.200',
          },
        }),
      },
    },
    Menu: {
      baseStyle: (props: { colorMode: string }) => ({
        list: {
          bg: props.colorMode === 'dark' ? 'stone.900' : 'white',
          borderColor: props.colorMode === 'dark' ? 'stone.700' : 'stone.200',
        },
        item: {
          bg: props.colorMode === 'dark' ? 'stone.900' : 'white',
          _hover: {
            bg: props.colorMode === 'dark' ? 'stone.800' : 'stone.100',
          },
        },
      }),
    },
    Heading: {
      baseStyle: {
        letterSpacing: '-0.025em',
      },
    },
    Badge: {
      baseStyle: {
        borderRadius: '9999px',
        fontFamily: `'Roboto', system-ui, sans-serif`,
        fontWeight: 500,
        fontSize: '12px',
        px: '10px',
        py: '2px',
      },
    },
  },
})

export default theme
